import Head from 'next/head';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import MarkDown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import styled, { css } from 'styled-components';

import { getSiteFromDomain } from 'server/utils';
import { withResponsiveContext } from '/imports/core/api/responsiveContext';
import { withIntl } from '/imports/core/api/useIntl';
import { withDomain } from '/imports/core/hooks/useDomain';
import { withTracking } from '/imports/core/hooks/useTracking';
import { replaceString } from '/imports/core/ui/helpers';
import {
  getCompanyRegistrationOffice,
  getCompanyCountryAdjective,
  getCompanyCountryByHost,
  getCompanyNameByHost,
  getCompanyNumber,
  getContactPageNumber,
  getLandingAddress,
  getRoute,
  isBlogDomain,
} from '/lib/helpers';

const newTC = (t, host, getBrandName, getContactEmail, locale, isNonFEF) => {
  const getClause19Slug = () => {
    const country = getCompanyCountryByHost(host);
    switch (country) {
      case 'Cyprus':
        return 'new_tc_que19_para1_cyprus';
      case 'France':
        return 'new_tc_que19_para1';
      default:
        return 'new_tc_que19_para1_NONFEF';
    }
  };
  return `
  <div style="text-align: center;">
  <h1>
   ${t('tc_heading')}  
  </h1>
  </div>
<div style="text-align: right;">
<p>
${t('new_tc_text1')}
</p>
</div>

  ${replaceString(t('new_tc_text2'), {
    COMPANY_NAME: getCompanyNameByHost(host),
    REGISTRATION_NUMBER: getCompanyNumber(host),
    COUNTRY_NAME: getCompanyCountryByHost(host),
    SITE_NAME: `[${getSiteFromDomain(host)}](${getSiteFromDomain(host)})`,
    DOMAIN_NAME: getBrandName(host),
    ADDRESS: getLandingAddress(host),
    COUNTRY_ADJECTIVE: getCompanyCountryAdjective(host),
    REGISTRATION_OFFICE: getCompanyRegistrationOffice(host),
    ...(!['GREAT PONTON LIMITED', 'GREAT PONTON COMPANY'].includes(getCompanyNameByHost(host)) && { 'D6W FN23': '' }),
  })}


${t('new_tc_text3')}

${t('new_tc_text4')}

${t('new_tc_text5')}


${t('new_tc_text6')}

## 1. ${t('new_tc_que1')}

${t('new_tc_que1_para1')}  

${t('new_tc_que1_para2')}  

## 2. ${t('new_tc_que2')}  

${replaceString(t('new_tc_que2_para1'), {
  CONTACT_URL: `[${getSiteFromDomain(host)}/contact-us](${getSiteFromDomain(host)}${getRoute('/contact-us', locale, false, host, isBlogDomain(host))})`,
})}  

## 3. ${t('new_tc_que3')}  

### 3.1. ${t('new_tc_que3_a')}

${t('new_tc_que3_a_para1')}

### 3.2 ${t('new_tc_que3_b')}

${t('new_tc_que3_b_para1')} 

${t('new_tc_que3_b_para2')}

${replaceString(t('new_tc_que3_b_para3'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que3_b_para4'), { DOMAIN_NAME: getBrandName(host) })}

${t('new_tc_que3_b_para5')}

${t('new_tc_que3_b_para6')}

${t('new_tc_que3_b_para7')}

### 3.3. ${t('new_tc_que3_c')}

${t('new_tc_que3_c_para1')}


3.4. ${t('new_tc_que3_d')}

${replaceString(t('new_tc_que3_d_para1'), {
  DOMAIN_NAME: getBrandName(host),
})}

## 4. ${t('new_tc_que4')}

${t('new_tc_que4_para1')}

${replaceString(t('new_tc_que4_para2'), {
  DOMAIN_NAME: getBrandName(host),
})}

- ${t('new_tc_que4_para3')}
- ${t('new_tc_que4_para4')}

- ${t('new_tc_que4_para5')}
- ${t('new_tc_que4_para6')}
- ${t('new_tc_que4_para7')}

- ${t('new_tc_que4_para8')}

- ${t('new_tc_que4_para9')}

${t('new_tc_que4_para10')}

## 5. ${t('new_tc_que5')}

### 5.1. ${t('new_tc_que5_a')}

${t('new_tc_que5_a_para1')}


${replaceString(t('new_tc_que5_a_para2'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que5_a_para3'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que5_a_para4'), {
  DOMAIN_NAME: getBrandName(host),
})}

### 5.2 ${t('new_tc_que5_b')}

${t('new_tc_que5_b_para1')}

${t('new_tc_que5_b_para2')}

${replaceString(t('new_tc_que5_b_para3'), {
  DOMAIN_NAME: getBrandName(host),
})}

### 5.3 ${t('new_tc_que5_c')}

${t('new_tc_que5_c_para1')}

${t('new_tc_que5_c_para2')}

${t('new_tc_que5_c_para3')}

### 5.4 ${t('new_tc_que5_d')}

${replaceString(t('new_tc_que5_d_para1'), {
  '/cancel-subscription': `${getRoute('/cancel-subscription', locale, false, host, isBlogDomain(host))}`,
})} [${getContactEmail(host)}](mailto:${getContactEmail(host)}).

${t('new_tc_que5_d_para2')}

${t('new_tc_que5_d_para3')}

${replaceString(t('new_tc_que5_d_para4'), {
  DOMAIN_NAME: getBrandName(host),
})}

${t('new_tc_que5_d_para5')}

${
  !isNonFEF
    ? `

### 5.5. ${t('new_tc_que5_e')}

${t('new_tc_que5_e_para1')}

<div class="style">

${t('new_tc_que5_e_para2')} 215-1

${t('new_tc_que5_e_para3')}

${t('new_tc_que5_e_para4')}

${t('new_tc_que5_e_para5')}

${t('new_tc_que5_e_para6')}

${t('new_tc_que5_e_para7')}

${t('new_tc_que5_e_para2')} 215-1-1  
${t('new_tc_que5_e_para8')}

${t('new_tc_que5_e_para9')}

${t('new_tc_que5_e_para10')}

${t('new_tc_que5_e_para2')} 215-2  
${t('new_tc_que5_e_para11')}

${t('new_tc_que5_e_para2')} 215-3  
${t('new_tc_que5_e_para12')}

${t('new_tc_que5_e_para2')} 241-3  
${t('new_tc_que5_e_para13')}
</div>
`
    : `

  `
}


## 6. ${t('new_tc_que6')}

${replaceString(t('new_tc_que6_para1'), {
  DOMAIN_NAME: getBrandName(host),
})}

${t('new_tc_que6_para2')}

## 7. ${t('new_tc_que7')}

${replaceString(t('new_tc_que7_para1'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que7_para2'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que7_para3'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que7_para4'), { DOMAIN_NAME: getBrandName(host) })}

## 8. ${t('new_tc_que8')}

${replaceString(t('new_tc_que8_para1'), { DOMAIN_NAME: getBrandName(host) })}

${replaceString(t('new_tc_que8_para2'), { DOMAIN_NAME: getBrandName(host) })}

## 9. ${t('new_tc_que9')}

${replaceString(t('new_tc_que9_para1'), { DOMAIN_NAME: getBrandName(host) })}

${t('new_tc_que9_para2')}

${t('new_tc_que9_para3')}

${t('new_tc_que9_para4')}

${replaceString(t('new_tc_que9_para5'), { DOMAIN_NAME: getBrandName(host) })}

## 10. ${t('new_tc_que10')}

${t('new_tc_que10_para1')}

## 11. ${t('new_tc_que11')}

${replaceString(t('new_tc_que11_para1'), { DOMAIN_NAME: getBrandName(host) })}

${replaceString(t('new_tc_que11_para2'), { DOMAIN_NAME: getBrandName(host) })}

- ${t('new_tc_que11_para3')}

- ${t('new_tc_que11_para4')}

- ${t('new_tc_que11_para5')}

- ${t('new_tc_que11_para6')}

- ${t('new_tc_que11_para7')}

- ${t('new_tc_que11_para8')}

## 12. ${t('new_tc_que12')}

${replaceString(t('new_tc_que12_para1'), { DOMAIN_NAME: getBrandName(host) })}

<div class="style">

${t('new_tc_que12_para2')}

${t('new_tc_que12_para3')}

${t('new_tc_que12_para4')}

${t('new_tc_que12_para5')}

1° ${t('new_tc_que12_para6')}

2° ${t('new_tc_que12_para7')}

3° ${t('new_tc_que12_para8')}

4° ${t('new_tc_que12_para9')}

5° ${t('new_tc_que12_para10')}

${t('new_tc_que12_para11')}

${t('new_tc_que12_para12')}

${t('new_tc_que12_para13')}

${
  !isNonFEF
    ? `
${replaceString(t('new_tc_que12_para14'), {
  LINK: 'https://www.legifrance.gouv.fr/affichCodeArticle.do?cidTexte=LEGITEXT000006069565&idArticle=LEGIARTI000044132839&dateTexte=&categorieLien=cid',
})}

${replaceString(t('new_tc_que12_para15'), {
  LINK: 'https://www.legifrance.gouv.fr/affichCodeArticle.do?cidTexte=LEGITEXT000006069565&idArticle=LEGIARTI000044138681&dateTexte=&categorieLien=cid',
})}

${replaceString(t('new_tc_que12_para16'), {
  LINK: 'https://www.legifrance.gouv.fr/affichCodeArticle.do?cidTexte=LEGITEXT000006070721&idArticle=LEGIARTI000006441924&dateTexte=&categorieLien=cid',
})}
`
    : `

`
}
</div>

## 13. ${t('new_tc_que13')}

${replaceString(t('new_tc_que13_para1'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que13_para2'), {
  DOMAIN_NAME: getBrandName(host),
  COUNTRY_ADJECTIVE: getCompanyCountryAdjective(host),
})}

${replaceString(t('new_tc_que13_para3'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que13_para4'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que13_para5'), {
  DOMAIN_NAME: getBrandName(host),
})}

## 14. ${t('new_tc_que14')}

${replaceString(t('new_tc_que14_para1'), {
  POLICY_URL: `${getRoute('/privacy-policy', locale, false, host, isBlogDomain(host))}`,
})}

## 15. ${t('new_tc_que15')}

${replaceString(t('new_tc_que15_para1'), {
  DOMAIN_NAME: getBrandName(host),
})}

## 16. ${t('new_tc_que16')}

### 16.1. ${t('new_tc_que16_a')}

${t('new_tc_que16_a_para1')}

${replaceString(t('new_tc_que16_a_para2'), {
  COMPANY_NAME: getCompanyNameByHost(host),
  COUNTRY: getCompanyCountryByHost(host),
  ADDRESS_NAME: getLandingAddress(host),
  CONTACT_EMAIL: `[${getContactEmail(host)}](mailto:${getContactEmail(host)})`,
  PHONE: getContactPageNumber(host)?.value,
  ...(!['GREAT PONTON LIMITED', 'GREAT PONTON COMPANY'].includes(getCompanyNameByHost(host)) && { 'D6W FN23': '' }),
})}


### 16.2. ${t('new_tc_que16_b')}

${t('new_tc_que16_b_para1')}

${t('new_tc_que16_b_para2')}

### 16.3. ${t('new_tc_que16_c')}

${t('new_tc_que16_c_para1')}

## 17. ${t('new_tc_que17')}

${t('new_tc_que17_para1')} [${getSiteFromDomain(host)}/refund-cancellation-policy](${getSiteFromDomain(host)}${getRoute('/refund-cancellation-policy', locale, false, host, isBlogDomain(host))})

## 18. ${t('new_tc_que18')}

${replaceString(t('new_tc_que18_para1'), {
  DOMAIN_NAME: getBrandName(host),
})}

${replaceString(t('new_tc_que18_para2'), { DOMAIN_NAME: getBrandName(host) })} [${getContactEmail(host)}](mailto:${getContactEmail(host)})


## 19. ${t('new_tc_que19')}

${t(getClause19Slug(host))}

${replaceString(t('new_tc_que19_para2'), {
  DOMAIN_NAME: getBrandName(host),
  CONTACT_EMAIL: `[${getContactEmail(host)}](mailto:${getContactEmail(host)})`,
})}

${replaceString(t('new_tc_que19_para3'), {
  DOMAIN_NAME: getBrandName(host),
  LINK_URL: 'https://ec.europa.eu/consumers/odr',
})} 


${t('new_tc_que19_para4')}


### ${t('new_tc_que20')}

${t('new_tc_que20_para1')}

${replaceString(t('new_tc_que20_para2'), {
  COMPANY_NAME: getCompanyNameByHost(host),
  ADDRESS_NAME: getLandingAddress(host),
})}

${['GREAT PONTON LIMITED', 'GREAT PONTON COMPANY'].includes(getCompanyNameByHost(host)) ? 'D6W FN23 –' : ''} [${getContactEmail(host)}](mailto:${getContactEmail(host)}):

${t('new_tc_que20_para3')}  
${t('new_tc_que20_para4')}    
${t('new_tc_que20_para5')}  
${t('new_tc_que20_para6')}   
${t('new_tc_que20_para7')}   
${t('new_tc_que20_para8')}

${t('new_tc_que20_para9')}
  `;
};

const StyledMarkDown = styled(MarkDown)`
  padding: 50px;
  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}

  p {
    font-size: 20px;
  }

  /* h1,
  h3 {
    text-align: center;
  } */

  h2 {
    margin-top: 25px;
  }
  .style {
    border: 1px solid #000;
    padding: 5px;
  }
`;

@withTracking
@withDomain
@withIntl
@withResponsiveContext
class TCPage extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
    t: PropTypes.func,
    host: PropTypes.string,
    getBrandName: PropTypes.func,
    getContactEmail: PropTypes.func,
    locale: PropTypes.string,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('terms_view');
  }

  render() {
    const { t, host, getBrandName, getContactEmail, locale } = this.props;
    const isNonFEF = !['modeles-cv', 'cvdeboss'].includes(host) && getCompanyNameByHost(host) !== 'FEF COMPANY';
    return (
      <React.Fragment>
        <Head>
          <meta name="robots" content="nofollow, noindex" />
        </Head>
        {host === 'cvdeboss' && (
          <Head>
            <title>Conditions générales - Cvdeboss.com</title>
          </Head>
        )}
        {host === 'modeles-cv' && (
          <Head>
            <title>Conditions générales - modeles-cv.fr</title>
          </Head>
        )}
        <StyledMarkDown
          children={newTC(t, host, getBrandName, getContactEmail, locale, isNonFEF)}
          rehypePlugins={[rehypeRaw]}
        />
      </React.Fragment>
    );
  }
}

export default TCPage;
